import React from "react";
import SeoLandingLayout from "./SeoLandingLayout";
import { BookOpen, Sparkles, Map as MapIcon, Users, Feather, Flame } from "lucide-react";
import { NARRATOR_LIST } from "@/data/models";

const FEATURES = [
  { icon: BookOpen, title: "Real branching, not menus", body: "You type what you want to do in your own words. The narrator writes the outcome. No fixed 4-option menus — the choice space is as wide as your imagination." },
  { icon: MapIcon, title: "Illustrated on every beat", body: "Automatic scene art on every major moment. Your adventure ends up as an illustrated book you can read back." },
  { icon: Sparkles, title: "Memory Codex", body: "NPCs, threads, milestones and inventory changes are tracked to help long adventures stay consistent." },
  { icon: Users, title: "Switchable AI narrators", body: `${NARRATOR_LIST}. Pick the voice that fits the mood.` },
  { icon: Feather, title: "Novel-quality prose", body: "Not chat-reply shorthand — literary sensory detail, dialogue in character, pacing that reads like a book." },
  { icon: Flame, title: "Free every day", body: "10 free turns/day on the Wanderer tier — no card. Enough to get well into your first adventure." },
];

const FAQ = [
  {
    q: "How does 'choose your own adventure AI' actually work here?",
    a: "The AI narrator writes each scene in literary prose, then either offers you a set of suggested choices or leaves the input wide open — you type what you want to do in your own words ('I sneak past the guards', 'I ask her about the shipment'). The AI narrates the outcome and the story branches from there. No fixed menus."
  },
  {
    q: "How is this better than a classic choose-your-own-adventure book?",
    a: "A book has ~30 branches. TalesTalesTales generates the story turn by turn, so the branch space is effectively infinite. Every playthrough is unique, illustrated, and your hero has stats and inventory tracked across chapters."
  },
  {
    q: "Can I share my adventure with someone else?",
    a: "You can publish your custom world to the community library so other players can adventure inside your setting. Direct chapter-sharing (read another player's finished saga) is on the roadmap."
  },
  {
    q: "What ages is this for?",
    a: "Standard sagas are written to be safe by default. There's an optional Mature Realms add-on for 18+ verified adult players, but it's opt-in and gated. Under-18 players stick to standard sagas."
  },
  {
    q: "Do I need an account?",
    a: "You can try 5 turns on the /trial page with no account. To keep going, register for a free Wanderer account — 10 turns per day, no card."
  },
];

export default function ChooseYourOwnAdventureAi() {
  return (
    <SeoLandingLayout
      testId="seo-cyoa-page"
      path="/choose-your-own-adventure-ai"
      title="Choose Your Own Adventure AI | TalesTalesTales"
      description="A choose your own adventure AI where every branch is generated live. Type what you do, an AI narrator writes the scene, illustrates it, and offers your next choice. Free daily turns."
      eyebrow="Choose Your Own Adventure AI"
      h1="Choose Your Own Adventure AI"
      h1Accent="where every branch is written for you"
      lede="TalesTalesTales is a choose-your-own-adventure AI built around real branching — not a fixed menu. Type what you want to do, an AI narrator writes the scene in literary prose, illustrates it, tracks your hero's state, and offers the next choice. Every playthrough is unique."
      body={(
        <>
          <p>
            Classic choose-your-own-adventure books had ~30 branches on a good day. TalesTalesTales
            generates the story <strong>turn by turn</strong>, so the branch space is effectively
            infinite. The AI narrator reads the state of your hero, the current scene, and the last
            few beats, then writes a chapter around whatever you type next.
          </p>
          <p>
            The <strong>choice model</strong> is open. You can pick from a set of suggested actions the
            narrator offers under each beat, or ignore them and type your own. &ldquo;I ask her about
            the shipment&rdquo;, &ldquo;I climb the cistern to get a better angle&rdquo;, &ldquo;I try
            to bluff my way past the guards with the fake seal&rdquo; — the narrator handles all of it
            and the story branches from there.
          </p>
          <p>
            Every major beat is <strong>illustrated automatically</strong>. Your adventure ends up as
            an illustrated book you can read back chapter by chapter. Sagas save automatically; you
            can return any time and continue from where you left off.
          </p>
          <p>
            The <strong>Memory Codex</strong> helps continuity across long adventures. NPCs,
            threads, milestones and inventory changes are tracked so the story is less likely to drift when
            you come back three weeks later — it doesn't guarantee perfect recall over very long sagas.
            Your hero has skills and inventory that carry forward — actions have consequences that
            carry forward.
          </p>
          <p>
            Light RPG elements (dice, HP, party companions) are optional. Turn them off for
            pure branching narrative, turn them on for stories shaped by the occasional random outcome. Most
            players end up with a hybrid: an illustrated choose-your-own-adventure novel driven by
            occasional dice.
          </p>
        </>
      )}
      features={FEATURES}
      faq={FAQ}
    />
  );
}
